/**
 * 放送地点一覧テーブル。
 *
 * - 行クリックまたは「編集」ボタンで編集モーダル (BroadcastPointForm) を開く。
 * - 「削除」は確認ダイアログを挟んで API DELETE を実行する。
 * - spotId はバックエンド管理項目のため、テーブルには表示しない。
 * - コンテンツは件数ではなく、設定済みの中身 (カテゴリ + 主要プロパティ) を一覧表示し、
 *   「何がどう設定されているか」を一目で確認できるようにする。
 * - 線区愛称 (`route_nickname`) は設定されないパターンの方が多いため独立列を持たず、
 *   設定されている場合のみ「線区#方面」列に括弧書きで併記する。
 */
import type {
  BroadcastContent,
  BroadcastPoint,
  ConnectingLineDelayByStationProperties,
  ConnectingLineDelayProperties,
  FreeTextProperties,
  SelfTrainDelayProperties,
} from '../api/types';
import { extractContents, getCategoryMeta, isEmptySsml } from '../utils/broadcastContents';

interface BroadcastPointsTableProps {
  points: BroadcastPoint[];
  onEdit: (point: BroadcastPoint) => void;
  onDelete: (point: BroadcastPoint) => void;
  /** 行をクリックした時に呼ばれる (地図フォーカス用)。任意。 */
  onRowClick?: (point: BroadcastPoint) => void;
}

export function BroadcastPointsTable({
  points,
  onEdit,
  onDelete,
  onRowClick,
}: BroadcastPointsTableProps): React.JSX.Element {
  if (points.length === 0) {
    return (
      <div className="empty-state">
        該当する放送地点はありません。「＋ 新規作成」から作成してください。
      </div>
    );
  }

  return (
    <div className="table-wrapper">
      <table className="points-table">
        <thead>
          <tr>
            <th>線区#方面</th>
            <th>説明</th>
            <th>半径(m)</th>
            <th>放送遅延(s)</th>
            <th>コンテンツ</th>
            <th aria-label="操作"></th>
          </tr>
        </thead>
        <tbody>
          {points.map((p) => {
            const contents = extractContents(p);
            const description = p.description ?? '';
            const nickname = (p.route_nickname ?? '').trim();
            return (
              <tr
                key={`${p.routeDirection}-${p.spotId}`}
                data-testid={`point-row-${p.spotId}`}
                data-description={description}
                style={onRowClick ? { cursor: 'pointer' } : undefined}
                onClick={onRowClick ? () => onRowClick(p) : undefined}
              >
                <td>
                  <span>{p.routeDirection}</span>
                  {nickname ? (
                    <span className="route-nickname" data-testid={`route-nickname-${p.spotId}`}>
                      （{nickname}）
                    </span>
                  ) : null}
                </td>
                <td>{description}</td>
                <td>{formatNumber(p.radius_meters, 0)}</td>
                <td>{p.broadcast_delay_seconds ?? ''}</td>

                <td>
                  {contents.length === 0 ? (
                    <span className="contents-empty">（なし）</span>
                  ) : (
                    <ul className="contents-cell">
                      {contents.map((c, i) => (
                        <li key={i}>{summarizeContent(c)}</li>
                      ))}
                    </ul>
                  )}
                </td>
                <td className="row-actions">
                  <button
                    type="button"
                    data-testid={`edit-button-${p.spotId}`}
                    aria-label={`${description || '(名称未設定)'} を編集`}
                    onClick={() => onEdit(p)}
                  >
                    編集
                  </button>
                  <button
                    type="button"
                    className="danger"
                    data-testid={`delete-button-${p.spotId}`}
                    aria-label={`${description || '(名称未設定)'} を削除`}
                    onClick={() => onDelete(p)}
                  >
                    削除
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function formatNumber(value: number | undefined, fractionDigits = 6): string {
  if (value === undefined || value === null || Number.isNaN(value)) return '';
  return value.toFixed(fractionDigits);
}

/**
 * コンテンツ 1 件を「カテゴリラベル + 主要プロパティ」の 1 行サマリ文字列にする。
 * 一覧テーブル上で中身を把握するための簡易表示。
 */
function summarizeContent(content: BroadcastContent): string {
  const meta = getCategoryMeta(content.category);
  const label = meta?.label ?? content.category;
  switch (content.category) {
    case 'self_train_delay': {
      const props = content.properties as Partial<SelfTrainDelayProperties>;
      const patternCount = Array.isArray(props.delay_patterns) ? props.delay_patterns.length : 0;
      return `${label}（${patternCount} パターン）`;
    }
    case 'connecting_line_delay': {
      const props = content.properties as Partial<ConnectingLineDelayProperties>;
      const routeId = props.route_id ? `route_id: ${props.route_id}` : 'route_id 未設定';
      const threshold =
        typeof props.delay_threshold_seconds === 'number'
          ? `しきい値 ${props.delay_threshold_seconds}秒`
          : 'しきい値 未設定';
      return `${label}（${routeId} / ${threshold}）`;
    }
    case 'connecting_line_delay_by_station': {
      // 接続線遅延 (乗換駅ベース / part2) は「乗換駅 (stop_id) / 接続線N件」の
      // 2 要素サマリで表示する。UI 実装上、乗換駅名までは事前展開しない
      // (Table 側で routes/routeStops マスタを持たないため) ので stop_id をそのまま出す。
      const props = content.properties as Partial<ConnectingLineDelayByStationProperties>;
      const transferStop = props.transfer_stop_id
        ? `乗換駅: ${props.transfer_stop_id}`
        : '乗換駅 未設定';
      const lineCount = Array.isArray(props.lines) ? props.lines.length : 0;
      return `${label}（${transferStop} / 接続線 ${lineCount}件）`;
    }
    case 'free_text': {
      const props = content.properties as Partial<FreeTextProperties>;
      const mode = resolveFreeTextMode(props);
      return `${label}（${mode}）`;
    }
    default:
      return label;
  }
}

/**
 * 任意テキストコンテンツの入力モードを表示用ラベルに変換する。
 *
 * `input_mode` 未指定 (旧データ) の場合の推測順は編集画面
 * (`broadcastContents/logic.ts` の `resolveInputMode`) と揃える。
 * 中身のない SSML (旧初期値の `<speak></speak>` など) は「SSML 未設定」とみなし、
 * 通常テキスト扱いにフォールバックする。
 */
function resolveFreeTextMode(props: Partial<FreeTextProperties>): string {
  const mode =
    props.input_mode ?? (props.audio_key ? 'audio' : !isEmptySsml(props.ssml) ? 'ssml' : 'text');
  switch (mode) {
    case 'audio':
      return '音声ファイル';
    case 'ssml':
      return 'SSML';
    default:
      return 'テキスト';
  }
}
